/**
 * Upload queue for event batches.
 *
 * V1 scope: in-memory ring buffer with retry. The full disk-backed flat-file
 * queue described in the spec lands with the native modules in Phase 4/5
 * (chunks must be on disk anyway). For events alone, in-memory is acceptable
 * because batches are flushed every 5 seconds and total event volume is small.
 *
 * Each enqueued batch carries a client-generated batch UUID. On flush, we
 * POST the batch to the API with batch_uuid; the server dedupes via the
 * UNIQUE chunk row. So a retry after a network blip is idempotent.
 */
import type { RecordedEvent } from "./types";

interface PendingBatch {
    visitId: number;
    sessionId: number;
    batchUuid: string;
    events: RecordedEvent[];
    attempts: number;
}

const queue: PendingBatch[] = [];
let isDraining = false;

function uuid(): string {
    // RFC4122 v4 with crypto.getRandomValues (Hermes 0.74+ has it).
    const arr = new Uint8Array(16);
    if (typeof crypto !== "undefined" && typeof crypto.getRandomValues === "function") {
        crypto.getRandomValues(arr);
    } else {
        for (let i = 0; i < 16; i++) {
            arr[i] = Math.floor(Math.random() * 256);
        }
    }
    arr[6] = (arr[6] & 0x0f) | 0x40;
    arr[8] = (arr[8] & 0x3f) | 0x80;
    const hex: string[] = [];
    for (let i = 0; i < 16; i++) {
        hex.push(arr[i].toString(16).padStart(2, "0"));
    }
    return `${hex.slice(0, 4).join("")}-${hex.slice(4, 6).join("")}-${hex.slice(6, 8).join("")}-${hex.slice(8, 10).join("")}-${hex.slice(10, 16).join("")}`;
}

export const UploadQueue = {
    /** Enqueue a batch of events for an active session. Returns the batch UUID. */
    enqueueEventBatch(visitId: number, sessionId: number, events: RecordedEvent[]): string {
        const batchUuid = uuid();
        queue.push({ visitId, sessionId, batchUuid, events, attempts: 0 });
        // Fire-and-forget drain.
        void UploadQueue.drain();
        return batchUuid;
    },

    pendingCount(): number {
        return queue.length;
    },

    async drain(): Promise<void> {
        if (isDraining) {
            return;
        }
        isDraining = true;
        try {
            while (queue.length > 0) {
                const batch = queue[0];
                const ok = await postBatch(batch);
                if (ok) {
                    queue.shift();
                } else {
                    batch.attempts += 1;
                    if (batch.attempts >= 5) {
                        // Drop after 5 failed attempts to avoid blocking the queue.
                        // The server has the JSONL chunk on S3 already (uploaded by
                        // the chunk pipeline), so dropping the Postgres-only batch
                        // is recoverable from the archive.
                        queue.shift();
                    } else {
                        const backoffMs = Math.min(30_000, 500 * 2 ** batch.attempts);
                        await new Promise((r) => setTimeout(r, backoffMs));
                    }
                }
            }
        } finally {
            isDraining = false;
        }
    },

    /** Test-only reset. */
    _reset(): void {
        queue.length = 0;
        isDraining = false;
    }
};

async function postBatch(batch: PendingBatch): Promise<boolean> {
    try {
        const { getValidToken, getApiBase } = await import("./api-bridge");
        const token = await getValidToken();
        if (!token) {
            return false;
        }
        const res = await fetch(`${getApiBase()}/visits/${batch.visitId}/recording/events`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`
            },
            body: JSON.stringify({
                sessionId: batch.sessionId,
                batchUuid: batch.batchUuid,
                events: batch.events
            })
        });
        if (res.ok) {
            return true;
        }
        // 4xx (other than 401) are non-retryable validation errors — drop them
        // from the queue rather than retrying forever.
        if (res.status >= 400 && res.status < 500 && res.status !== 401) {
            return true;
        }
        return false;
    } catch {
        return false;
    }
}
